import { apiVersions, getLogicEndpoint } from '@/services/endpoints';
import { isLocalMode } from '@/services/localRuntimeClient';
import { getArmBaseUrl } from '@/services/armConfig';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Error thrown by this module. Includes the HTTP status code and, when the
 * server returned a structured Azure error envelope, the `error.code` /
 * `error.message` from the response body.
 *
 * **Secret-safety contract:** `Error.message` never contains the request or
 * response body. `apiErrorMessage` / `apiErrorCode` are safe to render in
 * the UI but must NOT be passed to telemetry (defense in depth).
 */
export interface ObservabilityApiError extends Error {
  status: number;
  apiErrorCode?: string;
  apiErrorMessage?: string;
  requestId?: string;
}

/** Only supported value in v1 per the API guide. */
export type WiringMode = 'perAppEnvVar';

/** Only supported value in v1 per the API guide. */
export type SignalScope = 'all';

/** Application Insights configuration as returned by GET / PUT. */
export interface ApplicationInsightsConfig {
  connectionString: string;
  signalScope?: SignalScope;
}

/** Properties block as returned by GET or by PUT apply. */
export interface ObservabilityProperties {
  wiringMode: WiringMode;
  applicationInsights?: ApplicationInsightsConfig | null;
  /** ISO 8601 UTC. */
  updatedOn?: string;
  /** ISO 8601 UTC. Absent/null on fresh configs. */
  lastReconciledOn?: string | null;
}

/**
 * Response envelope. PUT Remove returns `{ properties: null }`; the GET
 * "not configured" path is a 404 (we surface it as `null` in the service).
 */
export interface ObservabilityConfig {
  name: string;
  type: string;
  id?: string;
  properties: ObservabilityProperties | null;
}

/** Discriminated input for the PUT mutation. */
export type ObservabilityPutInput = { kind: 'apply'; connectionString: string };

// =============================================================================
// HELPERS
// =============================================================================

/** In local mode, route to the mock control plane; otherwise to the regional Otto endpoint. */
const getBaseUrl = (): string => (isLocalMode() ? getArmBaseUrl() : getLogicEndpoint());

const buildUrl = (projectName: string): string =>
  `${getBaseUrl()}/automationProjects/${encodeURIComponent(projectName)}/observabilityConfiguration/default?api-version=${apiVersions.otto}`;

const buildHeaders = (token: string): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!isLocalMode()) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Builds a sanitized error from a failed response. The response body is
 * deliberately omitted from `Error.message` so telemetry never receives
 * it; the parsed Azure error envelope (when present) is attached as
 * separate structured fields for the UI to render.
 */
const buildApiError = async (
  op: 'fetch' | 'update' | 'delete',
  projectName: string,
  response: Response,
): Promise<ObservabilityApiError> => {
  const verb = op === 'fetch' ? 'fetch' : op === 'delete' ? 'delete' : 'update';
  const err = new Error(
    `Failed to ${verb} observability configuration for ${projectName}: HTTP ${response.status}`,
  ) as ObservabilityApiError;
  err.status = response.status;
  const requestId = response.headers?.get('x-ms-request-id') ?? undefined;
  if (requestId) err.requestId = requestId;

  // Best-effort parse of the standard Azure error envelope. We swallow
  // parse failures — a malformed body still results in a useful error
  // (status + requestId), and we never leak the raw body into Error.message.
  try {
    const body = await response.json();
    const inner = body?.error;
    if (inner && typeof inner === 'object') {
      if (typeof inner.code === 'string') err.apiErrorCode = inner.code;
      if (typeof inner.message === 'string') err.apiErrorMessage = inner.message;
    }
  } catch {
    /* ignore — non-JSON body */
  }
  return err;
};

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * GET the observability configuration for a project.
 * Returns `null` when no configuration is set (HTTP 404) — the caller
 * branches on `null` to render the "empty / setup" state. Any other
 * non-OK response throws an `ObservabilityApiError`.
 */
export const getObservabilityConfig = async (
  projectName: string,
  token: string,
): Promise<ObservabilityConfig | null> => {
  const response = await fetch(buildUrl(projectName), {
    method: 'GET',
    headers: buildHeaders(token),
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw await buildApiError('fetch', projectName, response);
  }

  return response.json();
};

/**
 * PUT the observability configuration for a project. Use this to create or
 * update the Application Insights wiring. To remove the configuration, use
 * `deleteObservabilityConfig` instead. The endpoint is synchronous (no
 * Azure-AsyncOperation header).
 */
export const putObservabilityConfig = async (
  projectName: string,
  token: string,
  input: ObservabilityPutInput,
): Promise<ObservabilityConfig> => {
  const body = {
    properties: {
      wiringMode: 'perAppEnvVar' as const,
      applicationInsights: {
        connectionString: input.connectionString,
      },
    },
  };

  const response = await fetch(buildUrl(projectName), {
    method: 'PUT',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await buildApiError('update', projectName, response);
  }

  return response.json();
};

/**
 * DELETE the observability configuration for a project. Idempotent: a 404
 * (already removed) is normalized to success so callers can safely retry.
 *
 * Resolves on 200 / 202 / 204 / 404 without returning a payload; any other
 * non-OK response throws a sanitized `ObservabilityApiError`.
 */
export const deleteObservabilityConfig = async (projectName: string, token: string): Promise<void> => {
  const response = await fetch(buildUrl(projectName), {
    method: 'DELETE',
    headers: buildHeaders(token),
  });

  // 404 = already gone. The DELETE verb is idempotent by contract, so
  // treat it the same as a successful removal rather than surfacing it as
  // an error to the caller.
  if (response.status === 404) return;

  if (!response.ok) {
    throw await buildApiError('delete', projectName, response);
  }
};
